"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import type { PostMeta } from "@/lib/blog";
import { track } from "@/lib/analytics";

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function matchesQuery(post: PostMeta, q: string): boolean {
  const needle = q.toLowerCase();
  return (
    post.title.toLowerCase().includes(needle) ||
    post.description.toLowerCase().includes(needle) ||
    post.tags.some((t) => t.toLowerCase().includes(needle))
  );
}

// SSR-safe read of the initial ?q= param. Returns "" on the server so the
// first client render matches the prerendered HTML.
function getInitialQuery(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("q") ?? "";
}

export default function BlogSearch({ posts }: { posts: PostMeta[] }) {
  const t = useTranslations("search");
  // Seed the input from ?q= via a lazy initializer (not a mount effect) so it
  // survives a shared/deep link. debouncedQuery stays "" for the first render
  // to match the prerendered HTML; the debounce effect below fills it in after
  // mount from inside its setTimeout callback, avoiding any hydration mismatch.
  const [query, setQuery] = useState(getInitialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce + update URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      if (query) {
        const count = posts.filter((p) => matchesQuery(p, query)).length;
        track("search", { search_term: query, results_count: count });
      }
      const url = new URL(window.location.href);
      if (query) {
        url.searchParams.set("q", query);
      } else {
        url.searchParams.delete("q");
      }
      window.history.replaceState({}, "", url.toString());
    }, 200);
    return () => clearTimeout(timer);
  }, [query, posts]);

  const filtered = debouncedQuery
    ? posts.filter((p) => matchesQuery(p, debouncedQuery))
    : posts;

  // Persistent live-region text so screen readers announce result changes.
  // Kept separate from the conditionally-rendered visible messages, which a
  // screen reader may miss when the element mounts at the same time it updates.
  const statusMessage = debouncedQuery
    ? filtered.length === 0
      ? t("noResults", { query: debouncedQuery })
      : `${filtered.length} ${
          filtered.length === 1 ? t("resultsSingle") : t("resultsPlural")
        }`
    : "";

  return (
    <div>
      {/* Search input */}
      <div className="relative mb-12">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
        <input
          type="search"
          placeholder={t("placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t("ariaLabel")}
          className="w-full pl-9 pr-4 py-2.5 bg-[var(--surface)] border border-white/10 rounded-lg text-base text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/60 focus:border-[var(--accent)]/40 transition-colors"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--text)] transition-colors"
            aria-label={t("clearAriaLabel")}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Persistent screen-reader status region */}
      <p className="sr-only" role="status" aria-live="polite">
        {statusMessage}
      </p>

      {/* Results */}
      {filtered.length === 0 ? (
        <p className="font-mono text-sm text-[var(--muted)]">
          {t("noResults", { query: debouncedQuery })}
        </p>
      ) : (
        <>
          {debouncedQuery && (
            <p className="font-mono text-xs text-[var(--muted)] mb-6">
              {filtered.length}{" "}
              {filtered.length === 1 ? t("resultsSingle") : t("resultsPlural")}{" "}
              for &ldquo;{debouncedQuery}&rdquo;
            </p>
          )}
          <ul className="divide-y divide-white/5">
            {filtered.map((post) => (
              <li key={post.slug} className="py-8 first:pt-0 last:pb-0">
                <div className="group relative flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-8">
                  <div className="font-mono text-xs text-[var(--muted)] shrink-0 sm:w-36 flex flex-col gap-0.5">
                    <time dateTime={post.date}>{formatDate(post.date)}</time>
                    {post.updated && post.updated !== post.date && (
                      <span>
                        Updated <time dateTime={post.updated}>{formatDate(post.updated)}</time>
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="font-display text-lg font-medium text-[var(--text)] group-hover:text-[var(--accent-text)] transition-colors mb-1.5 leading-snug">
                      {/* Stretched link: covers the card so tag links can sit on top without nesting <a> */}
                      <Link href={`/blog/${post.slug}`} className="after:absolute after:inset-0">
                        {post.title}
                      </Link>
                    </h2>
                    <p className="text-sm text-[var(--muted)] leading-relaxed">
                      {post.description}
                    </p>
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {post.tags.map((tag) => (
                          <Link
                            key={tag}
                            href={`/blog/tags/${tag}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              track("blog_tag_filter", { tag_name: tag });
                            }}
                            className="relative z-10 font-mono text-[0.65rem] uppercase tracking-wider text-[var(--accent-text)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full hover:bg-[var(--accent)]/20 transition-colors"
                          >
                            {tag}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
